import { Box, Button, Grid } from '@mui/material'
import React, { useEffect, useState } from 'react'
import EmptyTableRow from 'src/Component/EmptyTableRow'
import PaginatedTable, { HeadCellI, SortOrderI } from 'src/Component/PaginatedTable'
import DataRow from './DataRow'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'src/store'
import { startPageLoader, stopPageLoader } from 'src/store/features/common/commonSlice'
import { errorToast, successToast } from 'src/utils/toastService'
import SearchInput from 'src/Component/SearchInput'
import { useGetParentGymsListQuery, useDeleteParentGymMutation } from 'src/api/parentGymApi'
import AddParentGymModal from './AddParentGymModal'
import ConfirmationBox from 'src/Component/ConfirmationBox'
import ViewParentGymDetailsModal from './ViewParentGymDetailsModal'
import EditParentGymModal from './EditParentGymModal'
import TabComponent from 'src/Component/TabComponent'

const headCells: HeadCellI[] = [
  {
    id: 'id',
    isCheckBox: true,
    sortable: false,
    label: 'checkbox',
  },
  {
    id: 'name',
    sortable: true,
    label: 'Gym Name',
  },
  {
    id: 'branches_count',
    sortable: true,
    label: 'Branches',
  },
  {
    id: 'id',
    sortable: false,
    label: 'Action',
    align: 'center',
  },
]

type ParentGymProps = {
  tabs: string[]
  activeTab: number
  setActiveTab: (activeTab: number) => void
}
const ParentGym = ({ activeTab, setActiveTab, tabs }: ParentGymProps) => {
  const [page, setPage] = useState<number>(1)
  const [sortOrder, setSortOrder] = useState<SortOrderI>('asc')
  const [sortOrderBy, setSortOrderBy] = useState<string>('')
  const [openAddNewModal, setOpenAddNewModal] = useState(false)
  const [ParentGymDetailsModal, setParentGymDetailsModal] = useState({
    id: 0,
    open: false,
  })

  const [ParentGymDeleteConfirmation, setParentGymDeleteConfirmation] = useState({
    id: 0,
    open: false,
  })

  const [editParentGymModal, setEditParentGymModal] = useState({
    id: 0,
    open: false,
  })

  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [search, setSearch] = useState<string>('')

  // hooks
  const {
    data: GYM_DATA_RESPONSE,
    isFetching,
    refetch,
  } = useGetParentGymsListQuery(
    {
      search,
      page,
      pageSize: 10,
      sortBy: (() => {
        if (sortOrder === 'asc') {
          return sortOrderBy
        } else {
          return `-${sortOrderBy}`
        }
      })(),
    },
    { refetchOnMountOrArgChange: true },
  )
  const [deleteParentGym, { isLoading }] = useDeleteParentGymMutation()
  const dispatch = useDispatch<AppDispatch>()

  // functions
  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handleSorting = (_sortOrderBy: string) => {
    setSortOrder((prevState) => {
      return prevState === 'asc' ? 'desc' : 'asc'
    })
    setSortOrderBy(_sortOrderBy)
  }

  const handleParentGymDetailsModalClose = () => {
    setParentGymDetailsModal({
      id: 0,
      open: false,
    })
  }

  const handleViewDetailsClick = (id: number) => {
    setParentGymDetailsModal({
      id: id,
      open: true,
    })
  }

  const handleCloseDeleteConfirmation = () => {
    setParentGymDeleteConfirmation({
      id: 0,
      open: false,
    })
  }

  const handleOpenDeleteConfirmation = (id: number) => {
    setParentGymDeleteConfirmation({
      id: id,
      open: true,
    })
  }

  const handleEditParentGymModalClose = () => {
    setEditParentGymModal({
      id: 0,
      open: false,
    })
  }

  const handleEditParentGymClick = (id: number) => {
    setEditParentGymModal({
      id: id,
      open: true,
    })
  }

  const handleDeleteParentGym = () => {
    deleteParentGym(ParentGymDeleteConfirmation.id)
      .unwrap()
      .then((res) => {
        console.log(res)
        successToast('Parent Gym deleted successfully.')
        handleEditParentGymModalClose()
        refetch()
        setSelectedIds([])
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to delete Parent Gym.')
      })
      .finally(() => {
        handleCloseDeleteConfirmation()
      })
  }

  const handleSelectGym = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds((prevState) => {
        return prevState.filter((userId) => userId !== id)
      })
    } else {
      setSelectedIds((prevState) => {
        return [...prevState, id]
      })
    }
  }

  const handleSelectAllClick = (checked: boolean) => {
    if (checked && GYM_DATA_RESPONSE?.results.length) {
      setSelectedIds(GYM_DATA_RESPONSE.results.map((user) => user.id))
    } else {
      setSelectedIds([])
    }
  }

  const handleDeleteSelected = () => {
    Promise.all(selectedIds.map((id) => deleteParentGym(id).unwrap()))
      .then((res) => {
        console.log(res)
        successToast('Selected gyms deleted successfully.')
        refetch()
        setSelectedIds([])
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to selected delete gyms.')
      })
  }

  const handleSearch = (value: string) => {
    setPage(1)
    setSearch(value)
  }

  const onAddParentGymSuccessCallback = () => {
    setOpenAddNewModal(false)
    refetch()
  }

  const onEditParentGymSuccessCallback = () => {
    handleEditParentGymModalClose()
    refetch()
  }

  useEffect(() => {
    if (isFetching || isLoading) {
      dispatch(startPageLoader())
    } else {
      dispatch(stopPageLoader())
    }
  }, [isFetching, isLoading])

  useEffect(() => {
    setSelectedIds([])
  }, [GYM_DATA_RESPONSE])

  return (
    <Box className='pageContainer'>
      <Grid container>
        <Grid md={8} sm={12} xs={12}>
          {' '}
        </Grid>
        <Grid md={4} sm={12} xs={12}>
          <Box>
            <SearchInput hasDebounce onChange={handleSearch} />
          </Box>
        </Grid>
      </Grid>


      <Grid mt={10} container justifyContent='space-between'>
        <Grid item>
          {' '}
          <TabComponent
            options={tabs}
            activeIndex={activeTab}
            onTabIndexChange={setActiveTab}
          />
        </Grid>
        <Grid item>
          {' '}
          <Button
          variant='contained'
          color='primary'
          type='button'
          onClick={() => setOpenAddNewModal(true)}
        >
          ADD NEW
        </Button>
        </Grid>
      </Grid>

   

      <Box mt={2}>
        {GYM_DATA_RESPONSE && (
          <PaginatedTable
            title='Parent Gyms'
            headCells={headCells}
            currentPage={page}
            onPageChange={handlePageChange}
            totalPages={Math.ceil(GYM_DATA_RESPONSE?.count / 10)}
            onSortClick={handleSorting}
            onSelectAllClick={handleSelectAllClick}
            isAllSelected={
              GYM_DATA_RESPONSE.results.length === selectedIds.length && selectedIds.length !== 0
            }
            numSelected={selectedIds.length}
            onDeleteSelected={handleDeleteSelected}
          >
            <>
              {GYM_DATA_RESPONSE?.results?.length === 0 && (
                <EmptyTableRow cellCount={headCells.length} />
              )}
              {GYM_DATA_RESPONSE?.results?.map((user) => {
                return (
                  <DataRow
                    key={user.id}
                    id={user.id}
                    name={user.name}
                    branch={0}
                    selected={selectedIds.includes(user.id)}
                    onViewDetailsClick={handleViewDetailsClick}
                    onDeleteClick={handleOpenDeleteConfirmation}
                    onSelect={handleSelectGym}
                    onEditClick={handleEditParentGymClick}
                  />
                )
              })}
            </>
          </PaginatedTable>
        )}
      </Box>

      <AddParentGymModal
        open={openAddNewModal}
        onClose={() => setOpenAddNewModal(false)}
        successCallback={onAddParentGymSuccessCallback}
      />
      {!!ParentGymDetailsModal.id && (
        <ViewParentGymDetailsModal
          id={ParentGymDetailsModal.id}
          open={ParentGymDetailsModal.open}
          onClose={handleParentGymDetailsModalClose}
        />
      )}

      <ConfirmationBox
        title='Are you sure you want to Delete this gym.'
        open={ParentGymDeleteConfirmation.open}
        onReject={handleCloseDeleteConfirmation}
        onAccept={handleDeleteParentGym}
      />

      {!!editParentGymModal.id && (
        <EditParentGymModal
          id={editParentGymModal.id}
          open={editParentGymModal.open}
          onClose={handleEditParentGymModalClose}
          successCallback={onEditParentGymSuccessCallback}
          onDeleteClick={handleOpenDeleteConfirmation}
        />
      )}
    </Box>
  )
}

export default ParentGym
